import React, { useMemo, useState } from 'react';
import styles from './ModalStyles.module.scss';
import { Input } from '../../../../components/uikit/inputs/Input.tsx';
import { requiredRule } from '../../../../helpers/form/react-hook-form-helper.ts';
import { errorToString } from '../../../../helpers/error-helpers.ts';
import { ActionText } from '../../../../components/uikit/ActionText.tsx';
import { Loading } from '../../../../components/uikit/suspense/Loading.tsx';
import clsx from 'clsx';
import { defineDeviceImage } from '../../warranty-request/components/SerialNumberInfo.tsx';
import { Autocomplete, TextField } from '@mui/material';
import { SelectOption } from '../../warranty-request/components/SelectOption.tsx';
import { TextArea } from '../../../../components/uikit/inputs/TextArea.tsx';
import { AdvancedFormReturnType } from 'helpers/form/useAdvancedForm.ts';
import { Form, FoundDeviceOption, Option } from './AddDeviceModal.tsx';
import { useScopedTranslation } from '../../../../application/localization/useScopedTranslation.ts';
import ArrowDown from 'assets/icons/arrow-down-selector.svg?react';
import CloseIcon from 'assets/icons/close-button-small.svg?react';
import ResetIcon from 'assets/icons/reset.svg?react';
import { useWatch } from 'react-hook-form';
import { UseQueryResult } from '@tanstack/react-query';
import { RepairDeviceDto } from 'services/api/api-client.types.ts';
import { AxiosError } from 'axios';
import { getRepairReasonOptions } from '../repairReasonOptions.ts';
import { tDynamic } from '../../../../helpers/tDynamic.ts';
import { CustomModal } from '../../../../components/uikit/modal/CustomModal.tsx';
import { ButtonColor } from '../../../../components/uikit/buttons/Button.tsx';

type OwnProps = {
  form: AdvancedFormReturnType<Form>;
  isDeviceLoaded: boolean;
  deviceNotFound: boolean;
  hasMultipleDevices: boolean;
  device: UseQueryResult<RepairDeviceDto, AxiosError>;
  setSearchedSN: (newSN: string) => void;
  wasResetRef: React.RefObject<boolean>;
};

const maxDescriptionLength = 150;
const maxDeviceDescriptionLength = 100;

export const DeviceInfoScreen = (props: OwnProps) => {
  const {
    form,
    isDeviceLoaded,
    deviceNotFound,
    hasMultipleDevices,
    device,
    setSearchedSN,
    wasResetRef,
  } = props;

  const i18n = useScopedTranslation('Page.CreateRepairRequest');

  const [errorInputValue, setErrorInputValue] = useState('');
  const [isResetModalShown, setIsResetModalShown] = useState(false);

  const [
    serialNumber,
    selectedReasons,
    selectedErrors,
    selectedFoundDevice,
    deviceDescription,
    errorDescription,
  ] = useWatch({
    control: form.control,
    name: [
      'serialNumber',
      'selectedReasons',
      'selectedErrors',
      'selectedFoundDevice',
      'deviceDescription',
      'errorDescription',
    ],
  });

  const reasonOptions = getRepairReasonOptions(
    (key) => tDynamic(i18n, key),
    device.data?.services ?? [],
  );

  const errorOptions = useMemo<Option[]>(
    () =>
      Object.entries(device.data?.availableErrors ?? {}).map(([key, text]) => ({
        key,
        text,
      })),
    [device.data?.availableErrors],
  );

  const foundDeviceOptions = useMemo<FoundDeviceOption[]>(
    () =>
      (device.data?.foundDevices ?? []).map((item) => ({
        key: item.serviceProductId,
        text: `${item.serialNumber} - ${item.deviceName}`,
        serialNumber: item.serialNumber,
        article: item.article,
        deviceName: item.deviceName,
      })),
    [device.data?.foundDevices],
  );

  const handleSearchDevice = async () => {
    const normalizedSerialNumber = serialNumber.trim();
    form.setValue('serialNumber', normalizedSerialNumber, {
      shouldDirty: true,
    });

    const isSerialNumberValid = await form.trigger('serialNumber');

    if (!isSerialNumberValid) {
      return;
    }

    form.clearErrors('serialNumber');
    form.setValue('selectedFoundDevice', null);
    setSearchedSN(normalizedSerialNumber);
  };

  const removeSelectedError = (selectedError: Option) => {
    form.setValue(
      'selectedErrors',
      selectedErrors.filter((item) => item.key !== selectedError.key),
      {
        shouldDirty: true,
      },
    );
  };

  const removeSelectedReason = (selectedReason: Option) => {
    form.setValue(
      'selectedReasons',
      selectedReasons.filter((item) => item.key !== selectedReason.key),
      {
        shouldDirty: true,
      },
    );
  };

  const resetDevice = () => {
    setIsResetModalShown(false);
    setSearchedSN('');
    form.reset({
      serialNumber: '',
      selectedReasons: [],
      selectedErrors: [],
      selectedFoundDevice: null,
      receiptFile: null,
      errorDescription: '',
      deviceDescription: '',
      ticketId: '',
      repairLimitEuro: '',
      workRequiredImmediately: false,
      replacementReceived: null,
      receiptFileId: null,
      receiptFileName: null,
    });
    form.clearErrors();
    wasResetRef.current = true;
  };

  const deviceName =
    selectedFoundDevice?.deviceName || device.data?.deviceName || '';
  const deviceArticle =
    selectedFoundDevice?.article || device.data?.article || '';
  const deviceSerialNumber =
    selectedFoundDevice?.serialNumber ||
    device.data?.serialNumber ||
    serialNumber;

  const isError = device.isError;
  const isSNValid = !isError || (isError && deviceNotFound);
  const isDeviceLoading = device.isLoading;
  const isDeviceSelected =
    (!!selectedFoundDevice && !deviceNotFound) ||
    (isDeviceLoaded && !hasMultipleDevices && !deviceNotFound);

  const deviceImage = defineDeviceImage(deviceName, styles.icon);

  return (
    <div>
      {(!isDeviceLoaded || !isSNValid || isDeviceLoading) && (
        <div className={styles.addDeviceSearchRow}>
          <Input
            {...form.register('serialNumber', requiredRule(i18n.t('required')))}
            value={serialNumber}
            onChange={(e) => {
              form.setValue('serialNumber', e.target.value, {
                shouldDirty: true,
              });
              form.clearErrors('serialNumber');
            }}
            errorText={
              form.formState.errors.serialNumber?.message ||
              (device.isError ? errorToString(device.error) : undefined)
            }
            topHelperText={i18n.t('modal_serial_number')}
            topRightHelperText={i18n.t('required')}
            fullWidth
            onEnterPressed={handleSearchDevice}
            testId={'add-device-serial-input'}
          />
          <ActionText
            text={i18n.t('search')}
            onClickHandler={handleSearchDevice}
            className={styles.addDeviceSearchAction}
            testId={'add-device-serial-search'}
          />
          <Loading
            loading={device.isLoading}
            className={styles.absoluteContainer}
            wrapperClassName={styles.loader}
          />
        </div>
      )}

      {isSNValid && !isDeviceLoading && (
        <div className={styles.addDeviceModalBody}>
          {isDeviceLoaded && (
            <>
              <div className={styles.addDeviceRowHeader}>
                <div className={styles.addDeviceSectionTitle}>
                  {i18n.t('modal_device')}
                </div>
                <div
                  className={styles.addDeviceReset}
                  onClick={() => setIsResetModalShown(true)}
                >
                  <ResetIcon />
                  <span className={styles.fontWeight500}>
                    {i18n.t('reset')}
                  </span>
                </div>
              </div>
              {deviceImage || deviceNotFound ? (
                <div
                  className={clsx(
                    styles.deviceInfoCard,
                    styles.marginTopMinus24,
                  )}
                >
                  <div className={styles.deviceIconWrap}>
                    {deviceImage ??
                      defineDeviceImage(deviceName, styles.icon, true)}
                  </div>
                  <div className={styles.deviceInfoText}>
                    <div className={styles.deviceName}>{deviceName}</div>
                    <div className={styles.rowContainer20}>
                      <div className={styles.rowContainer}>
                        <div
                          className={clsx(
                            styles.deviceGreyText,
                            deviceNotFound && styles.addDeviceSectionTitle,
                          )}
                        >
                          {i18n.t('modal_sn')}
                        </div>
                        <div
                          className={clsx(
                            styles.text,
                            deviceNotFound && styles.addDeviceSectionTitle,
                          )}
                        >
                          {deviceSerialNumber}
                        </div>
                      </div>
                      {deviceArticle && (
                        <div className={styles.rowContainer}>
                          <div className={styles.deviceGreyText}>
                            {i18n.t('modal_article')}
                          </div>
                          <div className={styles.text}>{deviceArticle}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className={clsx(styles.serialNumberContainer)}>
                  <div className={clsx(styles.rowContainer)}>
                    <div className={styles.deviceGreyText}>
                      {i18n.t('modal_sn')}
                    </div>
                    <div className={styles.text}>{deviceSerialNumber}</div>
                  </div>
                  {deviceArticle && (
                    <div className={clsx(styles.rowContainer)}>
                      <div className={styles.deviceGreyText}>
                        {i18n.t('modal_device_type')}:{' '}
                      </div>
                      <div className={styles.text}>{deviceName}</div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {hasMultipleDevices && !selectedFoundDevice && (
            <div
              className={clsx(styles.addDeviceField, styles.comboBoxWrapper)}
            >
              <div className={styles.addDeviceLabelRow}>
                <div className={styles.addDeviceLabel}>
                  {i18n.t('modal_device_type')}
                </div>
                <div className={styles.addDeviceHelper}>
                  {i18n.t('required')}
                </div>
              </div>
              <Autocomplete
                data-test-id={'add-device-found-devices-select'}
                value={selectedFoundDevice}
                options={foundDeviceOptions}
                className={clsx(
                  styles.addDeviceDropdown,
                  !!form.formState.errors.selectedFoundDevice?.message &&
                    styles.addDeviceDropdownError,
                )}
                size="small"
                isOptionEqualToValue={(a, b) => a.key === b.key}
                onChange={(e, newValue) => {
                  form.setValue('selectedFoundDevice', newValue, {
                    shouldDirty: true,
                  });
                  form.clearErrors('selectedFoundDevice');
                }}
                getOptionLabel={(option) => option.text}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    className={styles.addDevicePlaceholder}
                    placeholder={i18n.t('modal_select_device_placeholder')}
                  />
                )}
                disableCloseOnSelect={true}
                popupIcon={
                  <ArrowDown onMouseDown={(e) => e.preventDefault()} />
                }
                disablePortal={true}
                renderOption={(props, option) => (
                  <SelectOption
                    testId={'device-item'}
                    key={option.key}
                    option={option}
                    liProps={props}
                    inputValue={''}
                  />
                )}
              />
              {!!form.formState.errors.selectedFoundDevice?.message && (
                <div className={styles.addDeviceFieldError}>
                  {form.formState.errors.selectedFoundDevice.message}
                </div>
              )}
            </div>
          )}

          {isDeviceSelected && (
            <>
              <div
                className={clsx(styles.addDeviceField, styles.comboBoxWrapper)}
              >
                <div className={styles.addDeviceLabel}>
                  {i18n.t('modal_reason_for_return')}
                </div>
                <Autocomplete
                  multiple
                  value={selectedReasons}
                  options={reasonOptions}
                  className={clsx(
                    styles.addDeviceDropdown,
                    !!form.formState.errors.selectedReasons?.message &&
                      styles.addDeviceDropdownError,
                  )}
                  size="small"
                  isOptionEqualToValue={(a, b) => a.key === b.key}
                  onChange={(e, newValue) => {
                    form.clearErrors('selectedReasons');
                    form.setValue('selectedReasons', newValue, {
                      shouldDirty: true,
                    });
                  }}
                  getOptionLabel={(option) => option.text}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={clsx(
                        styles.addDevicePlaceholder,
                        !!form.formState.errors.selectedReasons?.message
                          ? styles.fieldSetError
                          : styles.fieldSet,
                      )}
                      placeholder={i18n.t('modal_reason_placeholder')}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          readOnly: true,
                        },
                      }}
                    />
                  )}
                  disableCloseOnSelect={false}
                  popupIcon={
                    <ArrowDown onMouseDown={(e) => e.preventDefault()} />
                  }
                  disablePortal={true}
                  renderOption={(props, option) => (
                    <SelectOption
                      testId={'add-reason-item'}
                      key={option.key}
                      option={option}
                      liProps={props}
                      selectedOptions={selectedReasons}
                      inputValue={''}
                      optionBody={
                        <div className={styles.rowContainer}>
                          {option.text.split('(').map((text, index) => (
                            <div
                              key={text}
                              className={clsx(
                                styles.breakSpaces,
                                !!index && styles.addDeviceLabel,
                              )}
                            >
                              {!!index && ` (`}
                              {text}
                            </div>
                          ))}
                        </div>
                      }
                    />
                  )}
                />
                {!!selectedReasons.length && (
                  <div className={styles.addDeviceSelectedErrorContainer}>
                    {selectedReasons.map((reason) => (
                      <div
                        key={reason.key}
                        className={styles.addDeviceSelectedError}
                      >
                        <span>{reason.text.split('(').at(0)}</span>
                        <CloseIcon
                          className={styles.addDeviceSelectedErrorIcon}
                          onClick={() => removeSelectedReason(reason)}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {!!form.formState.errors.selectedReasons?.message && (
                  <div className={styles.addDeviceFieldError}>
                    {form.formState.errors.selectedReasons.message}
                  </div>
                )}
              </div>

              <div
                className={clsx(styles.addDeviceField, styles.comboBoxWrapper)}
              >
                <div className={styles.addDeviceLabelRow}>
                  <div className={styles.addDeviceLabel}>
                    {i18n.t('modal_error_name')}
                  </div>
                </div>
                <Autocomplete
                  multiple
                  data-test-id={'add-device-error-select'}
                  value={selectedErrors}
                  options={errorOptions}
                  className={styles.addDeviceDropdown}
                  size="small"
                  isOptionEqualToValue={(a, b) => a.key === b.key}
                  onChange={(e, newValue) => {
                    form.clearErrors('selectedErrors');
                    form.setValue('selectedErrors', newValue, {
                      shouldDirty: true,
                    });
                  }}
                  onInputChange={(e, value) => setErrorInputValue(value)}
                  getOptionLabel={(option) => option.text}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      className={clsx(
                        styles.addDevicePlaceholder,
                        !!form.formState.errors.selectedErrors?.message
                          ? styles.fieldSetError
                          : styles.fieldSet,
                      )}
                      placeholder={i18n.t('modal_error_placeholder')}
                    />
                  )}
                  disableCloseOnSelect={false}
                  popupIcon={
                    <ArrowDown onMouseDown={(e) => e.preventDefault()} />
                  }
                  disablePortal={true}
                  renderOption={(props, option) => (
                    <SelectOption
                      testId={'add-device-error-item'}
                      key={option.key}
                      option={option}
                      liProps={props}
                      selectedOptions={selectedErrors}
                      inputValue={errorInputValue}
                    />
                  )}
                />
                {!!selectedErrors.length && (
                  <div className={styles.addDeviceSelectedErrorContainer}>
                    {selectedErrors.map((error) => (
                      <div
                        key={error.key}
                        className={styles.addDeviceSelectedError}
                      >
                        <span>{error.text}</span>
                        <CloseIcon
                          className={styles.addDeviceSelectedErrorIcon}
                          onClick={() => removeSelectedError(error)}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {!!form.formState.errors.selectedErrors?.message && (
                  <div className={styles.addDeviceFieldError}>
                    {form.formState.errors.selectedErrors.message}
                  </div>
                )}
              </div>

              <TextArea
                value={errorDescription}
                onChange={(e) =>
                  form.setValue('errorDescription', e.target.value, {
                    shouldDirty: true,
                  })
                }
                topHelperText={i18n.t('modal_problem_description')}
                topRightHelperText={`${errorDescription.length}/${maxDescriptionLength}`}
                fullWidth
                className={styles.addDeviceProblemDescription}
                testId={'add-device-description'}
                maxLength={maxDescriptionLength}
              />
            </>
          )}

          {isDeviceLoaded && deviceNotFound && (
            <>
              <TextArea
                value={deviceDescription}
                onChange={(e) => {
                  form.setValue('deviceDescription', e.target.value, {
                    shouldDirty: true,
                  });
                  form.clearErrors('deviceDescription');
                }}
                topHelperText={i18n.t('modal_device_description')}
                fullWidth
                className={styles.addDeviceProblemDescription}
                placeholder={i18n.t('modal_describe_device')}
                errorText={form.formState.errors.deviceDescription?.message}
                topRightHelperText={`${deviceDescription?.length}/${maxDeviceDescriptionLength}`}
                maxLength={maxDeviceDescriptionLength}
              />
              <TextArea
                value={errorDescription}
                onChange={(e) => {
                  form.setValue('errorDescription', e.target.value, {
                    shouldDirty: true,
                  });
                  form.clearErrors('errorDescription');
                }}
                topHelperText={i18n.t('modal_error_description')}
                fullWidth
                className={styles.addDeviceProblemDescription}
                placeholder={i18n.t('modal_describe_error')}
                topRightHelperText={`${errorDescription?.length}/${maxDescriptionLength}`}
                errorText={form.formState.errors.errorDescription?.message}
                maxLength={maxDescriptionLength}
              />
            </>
          )}
        </div>
      )}
      <CustomModal
        isOpen={isResetModalShown}
        bodyText={i18n.t('reset_modal_body')}
        onSubmit={resetDevice}
        onCancel={() => setIsResetModalShown(false)}
        cancelButtonText={i18n.t('cancel')}
        submitButtonText={i18n.t('clear_fields')}
        titleText={i18n.t('clear_fields')}
        containerClassName={styles.modalBodyWidth480}
        submitButtonType={ButtonColor.Warning}
        cancelButtonType={ButtonColor.Secondary}
      />
    </div>
  );
};
